<template>
  <section class="responsible-prices">
    <div class="responsible-prices__box">
      <div class="responsible-prices__header">
        <h3>
          Valores referentes ao ano de
          <strong>{{ calendarYear }}</strong>
        </h3>
      </div>

      <div class="responsible-prices__content">
        <template v-if="pititimPrice || prices.length > 0">
          <div class="responsible-prices__grid">
            <div
              v-if="pititimPrice"
              class="responsible-prices__card responsible-prices__card--pititim"
            >
              <div class="responsible-prices__card-header">
                <h4>Pititim</h4>
                <span class="responsible-prices__description">
                  {{ formatBRL(pititimPrice.price) }} / módulo
                </span>
              </div>

              <div class="responsible-prices__card-body">
                <div class="responsible-prices__item">
                  <span class="responsible-prices__label">Taxa de material</span>
                  <span class="responsible-prices__value">
                    {{ formatOptionalBRL(pititimPrice.material_fee) }}
                  </span>
                </div>

                <div class="responsible-prices__divider"></div>

                <div class="responsible-prices__pititim-scroll">
                  <table class="responsible-prices__pititim-table">
                    <thead>
                      <tr>
                        <th>Módulos</th>
                        <th v-for="discount in pititimPrice.discounts" :key="'m-' + discount.modules">
                          {{ discount.modules }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Desconto</td>
                        <td v-for="discount in pititimPrice.discounts" :key="'d-' + discount.modules">
                          {{ discount.discount }}%
                        </td>
                      </tr>
                      <tr>
                        <td>Mensal</td>
                        <td v-for="discount in pititimPrice.discounts" :key="'v-' + discount.modules">
                          {{ formatBRL(calculateMonthly(discount.modules, discount.discount)) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div
              v-for="price in prices"
              :key="price.id"
              class="responsible-prices__card"
            >
              <div class="responsible-prices__card-header">
                <h4>{{ price.cycle_name }}</h4>
                <span class="responsible-prices__description">
                  {{ price.cycle_description }}
                </span>
              </div>

              <div class="responsible-prices__card-body">
                <div class="responsible-prices__item">
                  <span class="responsible-prices__label">12 parcelas</span>
                  <span class="responsible-prices__value">
                    {{ formatBRL(price.instalment_price) }}
                  </span>
                </div>

                <div class="responsible-prices__divider"></div>

                <div class="responsible-prices__item">
                  <span class="responsible-prices__label">Taxa de material</span>
                  <span class="responsible-prices__value">
                    {{ formatOptionalBRL(price.material_fee) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <div v-else class="responsible-prices__empty">
          <p>Valores não disponíveis no momento</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { formatBRL } from "@/utils/formatBRL";
import { calculateMonthlyPititimPrice } from "@/utils/pititimPrice";

export default {
  name: "ResponsiblePriceView",
  props: {
    prices: {
      type: Array,
      default: () => [],
    },
    calendarYear: {
      type: [String, Number],
      default: "",
    },
    pititimPrice: {
      type: Object,
      default: null,
    },
  },
  methods: {
    formatBRL,
    formatOptionalBRL(value) {
      return typeof value === "number" && Number.isFinite(value) && value > 0
        ? formatBRL(value)
        : "---";
    },
    calculateMonthly(modules, discount) {
      return calculateMonthlyPititimPrice(modules, this.pititimPrice.price, discount);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variablesSchool1.scss";

.responsible-prices {
  margin-bottom: 45px;

  &__title {
    color: $purple;
    margin-bottom: 20px;
    font-size: 24px;
    font-weight: 700;
  }

  &__box {
    border-radius: 12px;
    overflow: hidden;
    background: $white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &__header {
    background: linear-gradient(135deg, $green1, $blue);
    color: $white;
    padding: 20px 24px;
    text-align: center;

    h2,
    h3 {
      margin: 0;
      font-size: 22px;
      font-weight: 600;
      line-height: 1.4;
    }

    strong {
      font-weight: 700;
    }
  }

  &__content {
    background: $grey6;
    padding: 32px 24px;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 24px;
    justify-items: center;
  }

  &__card {
    background: $white;
    border-radius: 10px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    width: 100%;
    max-width: 280px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }

  &__card--pititim {
    grid-column: 1 / -1;
    max-width: 100%;
  }

  &__pititim-scroll {
    overflow-x: auto;
  }

  &__pititim-table {
    width: 100%;
    min-width: 480px;
    border-collapse: collapse;

    th,
    td {
      padding: 10px 14px;
      text-align: center;
      font-size: 16px;
      white-space: nowrap;
    }

    td:first-child,
    th:first-child {
      text-align: left;
    }

    thead th {
      background: $grey8;
      color: $blue;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      font-size: 14px;
      border-bottom: 2px solid $grey7;
    }

    tbody td {
      color: $grey3;
      border-bottom: 1px solid $grey7;
    }

    tbody td:first-child {
      color: $blue;
      font-weight: 700;
      text-transform: uppercase;
      font-size: 14px;
    }
  }

  &__card-header {
    background: linear-gradient(135deg, $green1, $blue);
    color: $white;
    padding: 16px 20px;
    border-radius: 10px 10px 0 0;
    height: 90px;

    h4 {
      margin: 0 0 4px 0;
      font-size: 16px;
      font-weight: 700;
    }
  }

  &__description {
    display: block;
    font-size: 13px;
    opacity: 0.9;
    line-height: 1.3;
  }

  &__card-body {
    padding: 20px;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
  }

  &__card--pititim &__item {
    justify-content: flex-start;
    gap: 8px;
  }

  &__label {
    color: $blue;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  &__value {
    color: $grey3;
    font-size: 16px;
    font-weight: 600;
  }

  &__divider {
    height: 1px;
    background: $grey5;
    margin: 4px 0;
  }

  &__empty {
    text-align: center;
    padding: 40px 20px;
    color: $grey4;
    font-size: 16px;
    font-weight: 600;
  }
}

@media (max-width: 576px) {
  .responsible-prices {
    &__grid {
      grid-template-columns: 1fr;
    }

    &__card {
      max-width: 100%;
    }
  }
}
</style>
